import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trialAPI } from '../../services/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import {
  Clock, Zap, AlertTriangle, X, Sparkles, ArrowRight
} from 'lucide-react';

const TrialBanner = ({ onClose }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    loadTrialStatus();
  }, []);

  const loadTrialStatus = async () => {
    try {
      const response = await trialAPI.getStatus();
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to load trial status');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (onClose) onClose();
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  // Don't show for premium users or if dismissed
  if (loading || dismissed || !status || status.is_premium) {
    return null;
  }

  // Trial expired - show upgrade prompt
  if (status.is_expired) {
    return (
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-5 w-5" />
            <div>
              <span className="font-medium">{status.message}</span>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              size="sm" 
              className="bg-white text-amber-700 hover:bg-amber-50"
              onClick={handleUpgrade}
            >
              <Sparkles className="h-4 w-4 mr-1" />
              Passer à Pro
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show warning (80%+ usage)
  if (status.show_warning) {
    return (
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Clock className="h-5 w-5" />
            <div className="flex items-center space-x-4">
              <span className="font-medium">{status.message}</span>
              <div className="hidden sm:flex items-center space-x-2 text-sm opacity-90">
                <Badge variant="outline" className="border-white/50 text-white">
                  {status.actions?.remaining} actions
                </Badge>
                <Badge variant="outline" className="border-white/50 text-white">
                  {status.time?.days_remaining} jours
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button 
              size="sm" 
              className="bg-white text-amber-600 hover:bg-amber-50"
              onClick={handleUpgrade}
            >
              Passer à Pro
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <button 
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal trial banner (compact)
  return (
    <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-4 py-2">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Zap className="h-4 w-4" />
          <span className="text-sm">
            <span className="font-medium">Essai gratuit</span>
            <span className="hidden sm:inline ml-2 opacity-90">
              {status.actions?.remaining}/{status.actions?.total} actions • {status.time?.days_remaining} jours restants
            </span>
          </span>
        </div>
        <div className="flex items-center space-x-3">
          <div className="hidden md:block w-32">
            <Progress 
              value={status.actions?.percent_used || 0} 
              className="h-1.5 bg-white/30"
            />
          </div>
          <Button 
            size="sm" 
            variant="ghost"
            className="text-white hover:bg-white/20 text-xs"
            onClick={handleUpgrade}
          >
            Voir les plans
          </Button>
          <button 
            onClick={handleDismiss}
            className="p-1 hover:bg-white/20 rounded"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
